import React, { useState, useMemo } from "react";
import { Box, useTheme } from "@mui/material";

import AumDonut from "../../Components/AumDonut";
import AiSectionFeedback from "../../Components/AiSectionFeedback";

import BookOfBusinessSection from "./BookOfBusinessSection";
import ClientPriorityTableSection from "./ClientPriorityTableSection";
import AiSuggestedPlanSection from "./AiSuggestedPlanSection";

const ClientPortfolioOverview = ({ data }) => {
  const theme = useTheme();

  const buildAumDistributionsFromApi = (data) => {
    const raw = data?.book_of_business_view?.aum_distribution || {};

    const LABEL_MAP = {
      fixed: "Fixed Income",
      private: "Private Markets",
      equities: "Equities",
      other: "Other",
    };

    const currentData = Object.entries(raw)
      .filter(([_, v]) => typeof v === "number" && v > 0)
      .map(([key, value]) => ({
        name: LABEL_MAP[key] || key,
        value: value * 100,
      }));

    return {
      current: {
        label: null,
        data: currentData,
      },
    };
  };

  const aumDistributions = buildAumDistributionsFromApi(data);

  const strategies = data?.book_of_business_view?.strategies || [];
  const clientPriorities = data?.client_priorities || [];
  const mv = data?.meetings_view || {};
  const shortPlan = mv?.talking_points?.short_term_plan || [];
  const longPlan = mv?.talking_points?.long_term_plan || [];

  const entityId =
    data?.client_id || data?.client_360_overview?.core?.client_name || "";

  const businessRows = useMemo(
    () =>
      strategies.map((s) => ({
        assetClass: s.asset_class,
        strategy: s.strategy,
        rating: s.buy_rating,
        healthLabel: s.health_label,
        vehicle: s.vehicle,
        aum: s.aum,
      })),
    [strategies]
  );

  const priorityRows = useMemo(
    () =>
      clientPriorities.map((p) => ({
        priority: p.priority_label,
        product: p.asset_class,
        retention: p.retention_goal,
        growth: p.growth_goal,
        service: p.service_goal,
        lead: p.primary_lead || "—",
      })),
    [clientPriorities]
  );

  const [healthState, setHealthState] = useState(
    businessRows.map((row) => ({
      assetClass: row.assetClass,
      strategy: row.strategy,
      value: row.healthLabel || "Healthy",
    }))
  );

  const healthOptions = [
    { value: "Healthy", label: "🟢 Healthy" },
    { value: "Caution", label: "🟡 Caution" },
    { value: "At Risk", label: "🔴 At Risk" },
  ];

  const getRowHealthValue = (strategy) => {
    const found = healthState.find((h) => h.strategy === strategy);
    return found ? found.value : "Healthy";
  };

  const handleHealthChange = (strategy, newValue) => {
    setHealthState((prev) =>
      prev.map((h) => (h.strategy === strategy ? { ...h, value: newValue } : h))
    );
  };

  const hasPlanSection = shortPlan.length > 0 || longPlan.length > 0;
  const requestId =
    data?.request_id ||
    data?.requestId ||
    data?.api_request_id ||
    data?.apiRequestId ||
    "12345";

  const userEmail = process.env.REACT_APP_USER;

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, sm: 3 },
        pt: 10,
        mt: 5,
      }}
    >
      <Box
        sx={{
          maxWidth: "96vw",
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {/* 1) BOOK OF BUSINESS */}
        <BookOfBusinessSection
          theme={theme}
          businessRows={businessRows}
          healthOptions={healthOptions}
          getRowHealthValue={getRowHealthValue}
          onHealthChange={handleHealthChange}
          aumDistributions={aumDistributions}
          AumDonut={AumDonut}
        />

        {/* 2) CLIENT PRIORITY TABLE (AI-reviewed) */}
        <ClientPriorityTableSection
          theme={theme}
          priorityRows={priorityRows}
          entityId={entityId}
          AiSectionFeedback={AiSectionFeedback}
          requestId={requestId}
          uiSectionKey="client_priorty"
          userEmail={userEmail}
        />

        {/* 3) AI-SUGGESTED PLAN (AI-reviewed) */}
        <AiSuggestedPlanSection
          theme={theme}
          hasPlanSection={hasPlanSection}
          shortPlan={shortPlan}
          longPlan={longPlan}
          entityId={entityId}
          AiSectionFeedback={AiSectionFeedback}
          requestId={requestId}
          uiSectionKey="ls_plans"
          userEmail={userEmail}
        />
      </Box>
    </Box>
  );
};

export default ClientPortfolioOverview;
